import { OutputDateType } from "statotest-api-requests";

import { captureException } from "../utils/sentry-helpers";

/**
 * Trvalá (cross-session) vrstva inkrementální cache naměřených dat (viz measuredDataCache.ts).
 * IndexedDB backing store — cache přežije reload / novou záložku / restart prohlížeče.
 *
 * Garantuje dvě tvrdé podmínky:
 *  - NEROSTE DO NEKONEČNA: vlastní bytový budget (PERSIST_BUDGET_BYTES) + LRU eviction podle
 *    wall-clock `lastUsed` + max-age drop + graceful `QuotaExceededError` → IDB je vždy omezené.
 *  - BEZ MEZER: persistuje PŘESNĚ contiguous CacheEntry tvar ({coveredFrom, coveredTo, rows});
 *    rehydratace ho vrátí 1:1 → stitching logika v measuredDataCache.ts se nemění → stejná
 *    korektnost jako in-memory. Živý ocásek (posledních LIVE_MARGIN_SEC) se nikdy nepersistuje,
 *    protože in-memory cache ukládá jen historickou (neměnnou) část → v IDB jsou jen neměnná data.
 *
 * Degrade na no-op, když IndexedDB není k dispozici (SSR / jsdom / private-mode selhání) →
 * stejná filozofie „0 negativních vlivů" jako in-memory cache.
 */

export interface PersistedEntry {
    key: string;
    coveredFrom: number;
    coveredTo: number;
    rows: OutputDateType[];
    bytesEst: number;
    lastUsed: number; // wall-clock (Date.now) — cross-session LRU
    savedAt: number;  // wall-clock — pro max-age drop
}

/** Odlehčená metadata (bez `rows`) — levné čtení pro eviction/hydrataci bez deserializace dat. */
interface IndexRecord {
    key: string;
    bytesEst: number;
    lastUsed: number;
    savedAt: number;
}

// --- budget & tunables ---
const MB = 1024 * 1024;
/**
 * IDB je DISKOVÉ úložiště → budget se řídí reálnou diskovou KVÓTOU originu
 * (`navigator.storage.estimate().quota`), NE RAM. Vezme malé % kvóty s absolutním stropem, ať se
 * přizpůsobí disku (na plném disku ustoupí, na prázdném povolí max cap). `estimate()` je async →
 * zjistí se jednou (`ensureDiskBudget`) a zacacheuje; sync `budgetBytes()` pak čte hodnotu.
 * Pozn.: persist/hydrate se stejně volají jen když je in-memory cache aktivní (getBudgetBytes>0
 * v measuredDataCache.ts) — slabé RAM zařízení se na disk vůbec nedostane (paměťová vrstva to gatuje).
 */
const DISK_BUDGET_CAP = 80 * MB;         // absolutní strop (i na obřím disku)
const DISK_QUOTA_FRACTION = 0.05;        // 5 % z reálné kvóty originu
const DISK_BUDGET_MIN_USEFUL = 10 * MB;  // pod tím persistence nemá smysl → 0
const DISK_BUDGET_FALLBACK = 64 * MB;    // estimate() nedostupné (starší prohlížeč)

let resolvedDiskBudget: number | null = null; // zjištěno 1× z estimate() (null = zatím neznámé)

const computeDiskBudget = async (): Promise<number> => {
    try {
        if (typeof navigator !== "undefined" && navigator.storage?.estimate) {
            const { quota } = await navigator.storage.estimate();
            if (typeof quota === "number" && quota > 0) {
                const b = Math.min(DISK_BUDGET_CAP, Math.floor(quota * DISK_QUOTA_FRACTION));
                return b < DISK_BUDGET_MIN_USEFUL ? 0 : b;
            }
        }
    }
    catch { /* estimate nedostupné/selhalo → fallback */ }
    return DISK_BUDGET_FALLBACK;
};

/** Zjistí diskový budget jednou (async z estimate()) a zacacheuje pro sync budgetBytes(). */
const ensureDiskBudget = async (): Promise<void> => {
    resolvedDiskBudget ??= await computeDiskBudget();
};

let budgetOverride: number | null = null;              // jen pro testy (__setPersistTuningForTests)
let maxAgeMs = 90 * 24 * 60 * 60 * 1000;               // 90 dní pojistka (persistence rozšiřuje „stale dataId" okno)
const budgetBytes = (): number => budgetOverride ?? resolvedDiskBudget ?? DISK_BUDGET_FALLBACK;

// Back-pressure při QuotaExceededError: reálná disková kvóta prohlížeče (private mode / plný disk)
// může být pod naším nominálním budgetem. Když put opakovaně padá na kvótu, přestaneme persistovat
// pro tuhle session (jinak by každý write-through znovu selhal + zaplavil Sentry). Cache jede dál in-memory.
let persistDisabled = false;
let consecutiveQuotaFailures = 0;
const QUOTA_FAILURE_LIMIT = 3;

const SCHEMA_VERSION = 1;   // bump → onupgradeneeded zahodí staré store (čistý start)
const DB_NAME = "statotest-charts-cache";
const STORE_ENTRIES = "entries";
const STORE_INDEX = "index";

const now = (): number => Date.now();

/** Raw async key-value backend — vyměnitelný pro testy (in-memory fake). */
export interface PersistenceBackend {
    loadIndex(): Promise<IndexRecord[]>;
    getEntry(key: string): Promise<PersistedEntry | undefined>;
    put(entry: PersistedEntry): Promise<void>;
    delete(keys: string[]): Promise<void>;
    clear(): Promise<void>;
}

// ── IndexedDB backend (default) ──────────────────────────────────────────────

const reqToPromise = <T>(r: IDBRequest<T>): Promise<T> =>
    new Promise((resolve, reject) => {
        r.onsuccess = (): void => resolve(r.result);
        r.onerror = (): void => reject(r.error ?? new Error("IDBRequest failed"));
    });

class IndexedDBBackend implements PersistenceBackend {
    private dbPromise: Promise<IDBDatabase | null> | null = null;

    private open(): Promise<IDBDatabase | null> {
        if (this.dbPromise) { return this.dbPromise; }
        this.dbPromise = new Promise((resolve) => {
            try {
                if (typeof indexedDB === "undefined" || !indexedDB) { resolve(null); return; }
                const req = indexedDB.open(DB_NAME, SCHEMA_VERSION);
                req.onupgradeneeded = (): void => {
                    const db = req.result;
                    // bump verze = čistý start (zahodit staré store)
                    for (const name of Array.from(db.objectStoreNames)) { db.deleteObjectStore(name); }
                    db.createObjectStore(STORE_ENTRIES, { keyPath: "key" });
                    db.createObjectStore(STORE_INDEX, { keyPath: "key" });
                };
                req.onsuccess = (): void => {
                    const db = req.result;
                    // Pusť budoucí upgrade z jiné záložky (bump SCHEMA_VERSION) — jinak by tahle
                    // otevřená connection blokovala upgrade a nová záložka spadla do onblocked.
                    db.onversionchange = (): void => { db.close(); this.dbPromise = null; };
                    resolve(db);
                };
                // onerror/onblocked mohou být přechodné (jiná záložka drží starou verzi) →
                // NEcachovat null natrvalo, umožnit příští open() retry.
                req.onerror = (): void => { this.dbPromise = null; resolve(null); };
                req.onblocked = (): void => { this.dbPromise = null; resolve(null); };
            }
            catch { this.dbPromise = null; resolve(null); }
        });
        return this.dbPromise;
    }

    public async loadIndex(): Promise<IndexRecord[]> {
        const db = await this.open();
        if (!db) { return []; }
        const tx = db.transaction(STORE_INDEX, "readonly");
        const res = await reqToPromise(tx.objectStore(STORE_INDEX).getAll() as IDBRequest<IndexRecord[]>);
        return Array.isArray(res) ? res : [];
    }

    public async getEntry(key: string): Promise<PersistedEntry | undefined> {
        const db = await this.open();
        if (!db) { return undefined; }
        const tx = db.transaction(STORE_ENTRIES, "readonly");
        return reqToPromise(tx.objectStore(STORE_ENTRIES).get(key) as IDBRequest<PersistedEntry | undefined>);
    }

    public async put(entry: PersistedEntry): Promise<void> {
        const db = await this.open();
        if (!db) { return; }
        const idx: IndexRecord = { key: entry.key, bytesEst: entry.bytesEst, lastUsed: entry.lastUsed, savedAt: entry.savedAt };
        const tx = db.transaction([STORE_ENTRIES, STORE_INDEX], "readwrite");
        const done = new Promise<void>((resolve, reject) => {
            tx.oncomplete = (): void => resolve();
            tx.onerror = (): void => reject(tx.error ?? new Error("IDB put tx failed"));
            tx.onabort = (): void => reject(tx.error ?? new Error("IDB put tx aborted"));
        });
        tx.objectStore(STORE_ENTRIES).put(entry);
        tx.objectStore(STORE_INDEX).put(idx);
        await done;
    }

    public async delete(keys: string[]): Promise<void> {
        if (keys.length === 0) { return; }
        const db = await this.open();
        if (!db) { return; }
        const tx = db.transaction([STORE_ENTRIES, STORE_INDEX], "readwrite");
        const done = new Promise<void>((resolve, reject) => {
            tx.oncomplete = (): void => resolve();
            tx.onerror = (): void => reject(tx.error ?? new Error("IDB delete tx failed"));
            tx.onabort = (): void => reject(tx.error ?? new Error("IDB delete tx aborted"));
        });
        for (const k of keys) {
            tx.objectStore(STORE_ENTRIES).delete(k);
            tx.objectStore(STORE_INDEX).delete(k);
        }
        await done;
    }

    public async clear(): Promise<void> {
        const db = await this.open();
        if (!db) { return; }
        const tx = db.transaction([STORE_ENTRIES, STORE_INDEX], "readwrite");
        const done = new Promise<void>((resolve, reject) => {
            tx.oncomplete = (): void => resolve();
            tx.onerror = (): void => reject(tx.error ?? new Error("IDB clear tx failed"));
            tx.onabort = (): void => reject(tx.error ?? new Error("IDB clear tx aborted"));
        });
        tx.objectStore(STORE_ENTRIES).clear();
        tx.objectStore(STORE_INDEX).clear();
        await done;
    }
}

let backend: PersistenceBackend = new IndexedDBBackend();

// ── Policy (budget / LRU / max-age) — backend-agnostic, testovatelné ─────────

/** Zahodí prošlé (max-age) a evikuje LRU, dokud součet bytesEst ≤ PERSIST_BUDGET_BYTES. */
const maintain = async (): Promise<void> => {
    const index = await backend.loadIndex();
    if (index.length === 0) { return; }

    const budget = budgetBytes();
    const cutoff = now() - maxAgeMs;
    const expired = index.filter((r) => r.savedAt < cutoff).map((r) => r.key);
    const live = index.filter((r) => r.savedAt >= cutoff);

    // LRU eviction přes budget (nejstarší lastUsed první)
    let total = live.reduce((s, r) => s + r.bytesEst, 0);
    const toEvict: string[] = [...expired];
    if (total > budget) {
        const byAge = [...live].sort((a, b) => a.lastUsed - b.lastUsed);
        for (const r of byAge) {
            if (total <= budget) { break; }
            toEvict.push(r.key);
            total -= r.bytesEst;
        }
    }
    if (toEvict.length > 0) { await backend.delete(toEvict); }
};

/**
 * Nouzová eviction při QuotaExceededError: reálná kvóta < náš nominální budget, takže `maintain`
 * (řízené naším budgetem) by neevikovalo nic. Zahodí nejstarší `fraction` entries (min. 1) BEZ
 * ohledu na budget, aby se uvolnilo místo na disku.
 */
const evictOldest = async (fraction: number): Promise<void> => {
    const index = await backend.loadIndex();
    if (index.length === 0) { return; }
    const byAge = [...index].sort((a, b) => a.lastUsed - b.lastUsed);
    const n = Math.max(1, Math.floor(index.length * fraction));
    await backend.delete(byAge.slice(0, n).map((r) => r.key));
};

/**
 * Načte persistovaná data pro hydrataci in-memory cache. Vrací MRU-first, oříznuté na `memBudget`
 * (aby hydratace neroznesla RAM). Předtím proběhne `maintain` (max-age + IDB budget).
 */
export const hydrate = async (memBudget: number): Promise<PersistedEntry[]> => {
    try {
        await ensureDiskBudget();
        if (budgetBytes() === 0) { return []; } // malá kvóta / persistence vypnutá
        await maintain();
        const index = await backend.loadIndex();
        if (index.length === 0) { return []; }

        const byRecent = [...index].sort((a, b) => b.lastUsed - a.lastUsed);
        const chosen: string[] = [];
        let total = 0;
        for (const r of byRecent) {
            if (total + r.bytesEst > memBudget) { continue; } // vejde-li se, jinak přeskoč (menší entry ještě může projít)
            chosen.push(r.key);
            total += r.bytesEst;
        }
        const entries = await Promise.all(chosen.map((k) => backend.getEntry(k)));
        return entries.filter((e): e is PersistedEntry => !!e);
    }
    catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)), { context: "measuredDataCachePersistence.hydrate" });
        return [];
    }
};

/** Write-through: uloží entry do IDB (fire-and-forget u volajícího). Stampuje lastUsed/savedAt. */
export const persist = async (entry: Omit<PersistedEntry, "lastUsed" | "savedAt">): Promise<void> => {
    if (persistDisabled) { return; }                    // back-pressure po opakované kvótě
    try {
        await ensureDiskBudget();
        const budget = budgetBytes();
        if (budget === 0) { return; }                  // malá kvóta → nepersistovat
        if (entry.bytesEst > budget) { return; }        // větší než celý budget → nemá smysl
        const t = now();
        await backend.put({ ...entry, lastUsed: t, savedAt: t });
        consecutiveQuotaFailures = 0;                   // úspěch → reset počítadla
        await maintain();
    }
    catch (e) {
        const isQuota = (e instanceof DOMException && (e.name === "QuotaExceededError" || e.code === 22))
            || (e instanceof Error && e.name === "QuotaExceededError");
        if (isQuota) {
            // Reálná kvóta < nominální budget → maintain by neuvolnil nic. Nouzově zahodit nejstarší.
            consecutiveQuotaFailures += 1;
            try { await evictOldest(0.25); } catch { /* ignore */ }
            if (consecutiveQuotaFailures >= QUOTA_FAILURE_LIMIT) {
                persistDisabled = true;                 // přestat hamrovat + Sentry flood; jen 1× nahlásit
                captureException(e instanceof Error ? e : new Error(String(e)),
                    { context: "measuredDataCachePersistence.persist.quota", cacheKey: entry.key });
            }
            return;
        }
        // Jiná chyba → zkusit uvolnit a degrade (cache jede dál in-memory)
        try { await maintain(); } catch { /* ignore */ }
        captureException(e instanceof Error ? e : new Error(String(e)), { context: "measuredDataCachePersistence.persist", cacheKey: entry.key });
    }
};

export const clearPersisted = async (): Promise<void> => {
    try { await backend.clear(); }
    catch (e) { captureException(e instanceof Error ? e : new Error(String(e)), { context: "measuredDataCachePersistence.clearPersisted" }); }
};

// ── Test helpers ─────────────────────────────────────────────────────────────

/** Jen pro testy: injektuje in-memory backend místo IndexedDB. `null` → reset na default. */
export const __setPersistenceBackendForTests = (b: PersistenceBackend | null): void => {
    backend = b ?? new IndexedDBBackend();
};

/** Jen pro testy: zmenší budget/max-age, ať jdou hranice ověřit malými daty. */
export const __setPersistTuningForTests = (opts: { budgetBytes?: number; maxAgeMs?: number }): void => {
    if (opts.budgetBytes !== undefined) { budgetOverride = opts.budgetBytes; }
    if (opts.maxAgeMs !== undefined) { maxAgeMs = opts.maxAgeMs; }
};

/** Jen pro testy: reset tunables — budget override, max-age, quota back-pressure i resolved disk budget. */
export const __resetPersistTuningForTests = (): void => {
    budgetOverride = null;
    maxAgeMs = 90 * 24 * 60 * 60 * 1000;
    persistDisabled = false;
    consecutiveQuotaFailures = 0;
    resolvedDiskBudget = null;
};

/** Jen pro testy: vynutí resolve diskového budgetu z navigator.storage.estimate() a vrátí ho. */
export const __getDiskBudgetForTests = async (): Promise<number> => {
    await ensureDiskBudget();
    return budgetBytes();
};

/** Jen pro testy: jednoduchý in-memory backend (dva „store" jako Mapy). */
export const __createInMemoryBackend = (): PersistenceBackend => {
    const entries = new Map<string, PersistedEntry>();
    return {
        loadIndex: (): Promise<IndexRecord[]> => Promise.resolve(
            [...entries.values()].map((e) => ({ key: e.key, bytesEst: e.bytesEst, lastUsed: e.lastUsed, savedAt: e.savedAt })),
        ),
        getEntry: (key: string): Promise<PersistedEntry | undefined> => Promise.resolve(
            entries.has(key) ? { ...entries.get(key)! } : undefined,
        ),
        put: (entry: PersistedEntry): Promise<void> => { entries.set(entry.key, { ...entry }); return Promise.resolve(); },
        delete: (keys: string[]): Promise<void> => { for (const k of keys) { entries.delete(k); } return Promise.resolve(); },
        clear: (): Promise<void> => { entries.clear(); return Promise.resolve(); },
    };
};
